import "./TaskCard.css";

import React from "react";

import { TaskTags } from "../data/TaskTags";
import { NO_DUE_DATE_LABEL, STATUS_LABELS, STATUS_MODIFIER, UNASSIGNED_ASSIGNEE_LABEL } from "./constants";
import { TaskCardContentProps, TaskCardMode, TaskCardProps, TaskCardStatus, TaskCardVariant } from "./models";
import { getDotModifier, getTaskType } from "./utils";

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const TaskCardSkeleton = ({
  variant = "seed",
  mode,
}: {
  variant?: TaskCardVariant;
  mode: TaskCardMode;
}): JSX.Element => (
  <div className={`TaskCard TaskCard_${variant} TaskCard_loading`} aria-busy="true" aria-label="Loading task">
    <div className="TaskCard_top">
      {mode === "kanban" && (
        <div className="TaskCard_skeletonTagRow">
          <span className="TaskCard_skeletonPill TaskCard_skeletonPill_72" />
          <span className="TaskCard_skeletonPill TaskCard_skeletonPill_56" />
          <span className="TaskCard_skeletonPill TaskCard_skeletonPill_40" />
        </div>
      )}
      <span className="TaskCard_skeletonLine TaskCard_skeletonLine_full TaskCard_skeletonLine_14" />
      <span className="TaskCard_skeletonLine TaskCard_skeletonLine_70 TaskCard_skeletonLine_14" />
      <span className="TaskCard_skeletonLine TaskCard_skeletonLine_80px TaskCard_skeletonLine_12" />
      {mode === "kanban" && (
        <div className="TaskCard_skeletonTagRow">
          <span className="TaskCard_skeletonPill TaskCard_skeletonPill_56" />
          <span className="TaskCard_skeletonPill TaskCard_skeletonPill_40" />
          <span className="TaskCard_skeletonPill TaskCard_skeletonPill_88" />
        </div>
      )}
    </div>
    <div className="TaskCard_bottom">
      <div className="TaskCard_skeletonUserRow">
        <span className="TaskCard_skeletonAvatar" />
        <span className="TaskCard_skeletonLine TaskCard_skeletonLine_80px TaskCard_skeletonLine_12" />
      </div>
      <div className="TaskCard_skeletonMetaRow">
        <span className="TaskCard_skeletonDot" />
        <span className="TaskCard_skeletonLine TaskCard_skeletonLine_64px TaskCard_skeletonLine_12" />
      </div>
    </div>
  </div>
);

// ─── Sub-renders ──────────────────────────────────────────────────────────────

/**
 * The linked parent object. A link only when the caller supplied somewhere to go: without a
 * handler the name is still worth showing as context, but must not present as clickable.
 */
const TaskCardObjectLine = ({
  linkedObjectName,
  onObjectClick,
}: {
  linkedObjectName?: string;
  onObjectClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}): JSX.Element | null => {
  if (!linkedObjectName) {
    return null;
  }

  if (!onObjectClick) {
    return <p className="TaskCard_objectName">{linkedObjectName}</p>;
  }

  return (
    <button
      className="TaskCard_objectLink"
      onClick={(e) => {
        e.stopPropagation();
        onObjectClick(e);
      }}
      aria-label={`Open ${linkedObjectName}`}
    >
      {linkedObjectName}
    </button>
  );
};

/**
 * The assignee, or an explicit unassigned treatment. The empty avatar keeps the row the same
 * height either way, so a list of cards does not jitter as assignment changes.
 */
const TaskCardAssignee = ({
  assigneeInitials,
  assigneeName,
}: {
  assigneeInitials?: string;
  assigneeName?: string;
}): JSX.Element => {
  if (!assigneeName) {
    return (
      <div className="TaskCard_userRow">
        <div className="TaskCard_avatar TaskCard_avatar_empty" aria-hidden />
        <span className="TaskCard_assigneeName TaskCard_assigneeName_unassigned">{UNASSIGNED_ASSIGNEE_LABEL}</span>
      </div>
    );
  }

  return (
    <div className="TaskCard_userRow">
      <div className="TaskCard_avatar" aria-hidden>
        {assigneeInitials}
      </div>
      <span className="TaskCard_assigneeName">{assigneeName}</span>
    </div>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

export const TaskCard = (props: TaskCardProps): JSX.Element => {
  const { variant = "seed", mode, state = "default" } = props;

  if (state === "loading") {
    return <TaskCardSkeleton variant={variant} mode={mode} />;
  }

  const {
    isDragging = false,
    draggable = false,
    onDragStart,
    onDragEnd,
    title,
    linkedObjectName,
    linkedObjectType,
    onObjectClick,
    onClick,
    assigneeInitials,
    assigneeName,
    dueDate,
    daysOverdue,
    isMandatory,
    isMilestone,
    hasWorkflow,
    isGeneral,
    tags,
    showStatus,
    status,
  } = props as TaskCardContentProps;

  const isOverdue = state === "overdue";
  const isKanban = mode === "kanban";
  const isSeed = variant === "seed";
  const extraTags = tags ?? [];

  const rootClasses = [
    "TaskCard",
    `TaskCard_${variant}`,
    isKanban ? "TaskCard_kanban" : "TaskCard_milestoneKpi",
    isOverdue ? "TaskCard_overdue" : "",
    isDragging ? "TaskCard_dragging" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  const taskType = getTaskType(hasWorkflow, isGeneral);
  const dotModifier = getDotModifier(dueDate);

  const showTagRow =
    isKanban && (isMandatory || isMilestone || taskType || (isSeed && linkedObjectType) || extraTags.length > 0);

  return (
    <div
      className={rootClasses}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      aria-label={`Task: ${title}`}
    >
      <div className="TaskCard_top">
        {showTagRow && (
          <TaskTags
            isMandatory={isMandatory}
            isMilestone={isMilestone}
            taskType={taskType}
            objectType={isSeed ? linkedObjectType : undefined}
            extraTags={extraTags}
          />
        )}

        <p className="TaskCard_title">{title}</p>

        <TaskCardObjectLine linkedObjectName={linkedObjectName} onObjectClick={onObjectClick} />
      </div>

      <div className="TaskCard_bottom">
        {isKanban ? (
          <TaskCardAssignee assigneeInitials={assigneeInitials} assigneeName={assigneeName} />
        ) : (
          <div className="TaskCard_userStatusRow">
            <TaskCardAssignee assigneeInitials={assigneeInitials} assigneeName={assigneeName} />
            {showStatus && status != null && (
              <span className={`TaskCard_statusTag ${STATUS_MODIFIER[status as TaskCardStatus]}`}>
                {STATUS_LABELS[status as TaskCardStatus]}
              </span>
            )}
          </div>
        )}

        <div className="TaskCard_metaRow">
          {dotModifier && <span className={`TaskCard_dot ${dotModifier}`} aria-hidden />}
          <span className={`TaskCard_dueDate${isOverdue ? " TaskCard_dueDate_overdue" : ""}`}>
            {dueDate ?? NO_DUE_DATE_LABEL}
          </span>
          {isOverdue && isKanban && daysOverdue !== undefined && (
            <span className="TaskCard_overdueDays">• {daysOverdue} days overdue</span>
          )}
        </div>
      </div>
    </div>
  );
};
